import { publicAssetUrl } from "@/lib/public-image-url";
import type { ComparisonGuide } from "./types";

const UPDATED = "2026-05-14";

export const COMPARISON_GUIDES: ComparisonGuide[] = [
  {
    slug: "classic-vs-modern-convertible-road-trip",
    title: "Classic vs Modern Convertible for a Road Trip: Honest Comparison",
    metaTitle: "Classic vs Modern Convertible for a Road Trip: Which to Hire?",
    metaDescription:
      "Side-by-side comparison of classic and modern convertible hire for a multi-day road trip: comfort, reliability, cost, photographs, and the right pick by trip length.",
    tldr:
      "Classic convertibles win on photos, sound, and the experience of driving. Modern convertibles win on comfort, reliability, and bad-weather performance. For trips of one to three days, a classic is usually the right call. For trips longer than three days or in unpredictable weather, lean modern.",
    hero: {
      image: publicAssetUrl("cars/1049224/legacy-1172061_1280w.webp"),
      alt: "Morgan Plus 4 convertible listed for self-drive hire",
    },
    optionA: "Classic convertible (1960s–1980s)",
    optionB: "Modern convertible (2010s–present)",
    table: [
      { criterion: "Daily comfort over 6+ hours", a: "Firm, narrow cabin, loud above 70 mph", b: "Quiet, climate-controlled, supportive seats" },
      { criterion: "Reliability on a 1,000-mile trip", a: "Plan for 1–2 minor issues; carry the owner's number", b: "Essentially zero unplanned stops" },
      { criterion: "Wet-weather performance", a: "Wipers and demisters are 1960s-era; soft-tops can leak", b: "Modern wipers, demist, traction control" },
      { criterion: "Daily hire cost", a: "£250–£900/day", b: "£150–£400/day" },
      { criterion: "Photographs", a: "Iconic; ages well", b: "Generic" },
      { criterion: "Sound", a: "Mechanical, distinctive, exhaust-led", b: "Largely synthesised, quiet" },
      { criterion: "Luggage capacity", a: "Minimal (one weekend bag each)", b: "Practical (two suitcases)" },
      { criterion: "Crash safety", a: "1960s-era; no airbags, modest crumple zones", b: "Modern airbags, ABS, ESC, lane assist" },
      { criterion: "Servicing on the road", a: "Limited specialist availability outside major cities", b: "Any dealer can help" },
    ],
    sections: [
      {
        heading: "When the classic is the right call",
        body:
          "One- to three-day trips in fair weather, on roads you've driven before. Trips where the photographs are part of the point, such as anniversary trips, honeymoons, and milestone birthdays. Trips with a planned overnight pace (under 200 miles/day) and lunch stops, not 600-mile motorway days.",
      },
      {
        heading: "When the modern wins",
        body:
          "Long trips (4+ days) in unpredictable weather. Trips with significant motorway content. Trips with luggage. Trips where you'll arrive tired (long-haul flight + drive). Family trips with children are less suitable because modern car seats fit modern cars.",
      },
      {
        heading: "The hybrid approach",
        body:
          "A common compromise: rent a modern convertible for transit days (airport → starting point, end point → airport) and hire a classic locally for the two best driving days in the middle. BookAclassic can handle the classic leg; major rental firms handle the transit.",
      },
    ],
    faqs: [
      {
        q: "Will a classic break down on a 3-day trip?",
        a: "Usually not, if the owner has been honest about the car's recent service. Plan for occasional minor issues (overheating in traffic, a recalcitrant gearbox synchro). Carry the owner's mobile so you can coordinate directly if anything goes wrong.",
      },
      {
        q: "What about insurance differences?",
        a: "Both are covered under BookAclassic's standard cover for self-drive hires. Modern rentals from major firms typically have higher deductibles and more exclusions than a classic peer-to-peer hire.",
      },
    ],
    recommendation:
      "For a memorable 2–3 day trip, hire a classic. For a 5+ day Alpine grand tour, hire a modern convertible and add a one-day classic experience at the most scenic stop.",
    listingsQuery: { rental_type: "driving" },
    related: {
      useCases: ["driving-experience-hire", "anniversary-gift-experience"],
      models: ["porsche-911-air-cooled", "jaguar-e-type", "mercedes-pagoda"],
      cities: ["cotswolds", "cote-dazur"],
    },
    updatedAt: UPDATED,
  },
  {
    slug: "self-drive-vs-chauffeured-classic",
    title: "Self-Drive vs Chauffeured Classic Car Hire: Which Should You Pick?",
    metaTitle: "Self-Drive vs Chauffeur-Driven Classic Car Hire: Honest Comparison",
    metaDescription:
      "Side-by-side comparison of self-drive and chauffeur-driven classic car hire: cost, insurance, control, and which occasions favour each.",
    tldr:
      "Self-drive is the right call when you want to drive the car as part of the experience and you meet the age/licence requirements. Chauffeured is the right call for weddings, formal events, evening occasions, and situations where parking, traffic, or formal dress make driving impractical.",
    hero: {
      image: publicAssetUrl("cars/1043138/legacy-1122428_1280w.webp"),
      alt: "Rolls-Royce Silver Shadow available with a chauffeur",
    },
    optionA: "Self-drive hire",
    optionB: "Chauffeured hire",
    table: [
      { criterion: "Day rate", a: "£250–£900 typical", b: "£400–£1,200 typical (chauffeur included)" },
      { criterion: "Insurance excess", a: "£500–£5,000 deposit pre-authorised", b: "Zero: owner carries risk" },
      { criterion: "Minimum age", a: "25–35 depending on car", b: "No minimum (you're a passenger)" },
      { criterion: "Licence checks", a: "Required (DVLA or international)", b: "Not required" },
      { criterion: "Control of route", a: "Total", b: "Pre-agreed with the chauffeur" },
      { criterion: "Suitable for weddings", a: "Vow renewals, small registry-office", b: "Standard for traditional ceremonies" },
      { criterion: "Suitable for evening events", a: "Less common", b: "Standard" },
      { criterion: "Photographer wants moving shots", a: "Easier: you can stop on demand", b: "Easier: the chauffeur drives, so you're in the photo" },
    ],
    sections: [
      {
        heading: "Self-drive: when it's right",
        body:
          "Self-drive is the right call when driving the car is part of the experience: anniversary drives, milestone birthdays, weekend tours, small informal weddings, photo shoots where the couple wants to drive. You'll need to be 25–35 (depending on the car), hold a valid licence, and accept a security deposit.",
      },
      {
        heading: "Chauffeured: when it's right",
        body:
          "Chauffeured is the right call for traditional weddings, prom and formal events, evening occasions (theatre, gala), and any situation where parking or formal dress make driving impractical. The chauffeur arrives 30 minutes early, dressed appropriately, with the car prepared.",
      },
      {
        heading: "Cost: what the difference actually is",
        body:
          "Chauffeured packages cost more on paper, but the chauffeur's time, fuel, parking, and overtime contingency are included. Self-drive is cheaper on the headline rate but adds fuel, deposit, mileage overage, and the personal time spent driving. For a 4-hour event, the all-in cost is often within 20% of each other.",
      },
    ],
    faqs: [
      {
        q: "Can I switch between self-drive and chauffeured mid-booking?",
        a: "Some operators offer a 'chauffeur to the venue, self-drive after the ceremony' option for weddings. Ask the owner directly.",
      },
      {
        q: "Does the chauffeur stay with the car the whole time?",
        a: "Yes. They wait at or near the venue, handle parking, and are responsible for the car. Most weddings book a 4-hour window covering ceremony, photos, and reception transfer.",
      },
      {
        q: "Can I tip the chauffeur?",
        a: "Customary in the UK and US (10–15%) and appreciated everywhere, but not required.",
      },
    ],
    recommendation:
      "Weddings, prom, evening galas, and formal corporate events → chauffeured. Anniversary drives, weekend tours, photo shoots, and informal events → self-drive.",
    listingsQuery: { rental_type: "chauffeured" },
    related: {
      useCases: ["wedding-car-hire", "driving-experience-hire"],
      models: ["rolls-royce-silver-shadow", "jaguar-e-type"],
      cities: ["london", "paris"],
    },
    updatedAt: UPDATED,
  },
];

export function getComparisonGuide(slug: string): ComparisonGuide | undefined {
  return COMPARISON_GUIDES.find((c) => c.slug === slug);
}
